import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import type {
  HandoverItineraryOption,
  OrderHandoverContractApplyPayload,
  OrderHandoverContractInvalidatePayload,
  OrderHandoverContractSignPortal,
  OrderHandoverContractSignPreparePayload,
  OrderHandoverContractSignSimulationPayload,
  OrderHandoverContractSigning,
  OrderHandoverDetail,
  OrderHandoverCustomerQuoteOption,
  OrderHandoverMajorOption,
  OrderHandoverOrderLine,
  OrderHandoverRosterExportType,
  OrderHandoverRosterExportOptions,
  OrderHandoverRosterImportPayload,
  OrderHandoverRosterImportPreview,
  OrderHandoverRosterMemberCreatePayload,
  OrderHandoverRosterMemberFlagsPayload,
  OrderHandoverRosterMemberStopsPayload,
  OrderHandoverRosterMemberUpdatePayload,
  GuideLaborContract,
  GuideLaborContractGeneratePayload,
  OrderHandoverSalesMetadata,
  OrderHandoverSalesHandoverUpdate,
  OrderHandoverStaffOption,
  OrderRosterMember,
} from "../types";
import { rosterStandardTemplateFileName } from "../config";

const ROSTER_IMPORT_TIMEOUT_MS = 60_000;
const ORDER_HANDOVER_DETAIL_TIMEOUT_MS = 60_000;

type SysStaffListPayloadRaw = {
  items?: Array<{
    id: number;
    username: string;
    realname: string;
    nickname: string;
    main_dept_name: string;
    status: number;
  }>;
};

type DicMajorListPayloadRaw = {
  items?: Array<{
    major_id: string;
    major_name: string;
    major_category: string;
    is_disabled: boolean;
  }>;
};

type OrderHandoverSalesMetadataRaw = {
  service_consultant_options: OrderHandoverSalesMetadataStaffOptionRaw[];
  staff_options: OrderHandoverSalesMetadataStaffOptionRaw[];
  school_site_options: Array<{
    value: string;
    site_id: string | null;
    site_name: string | null;
    address: string | null;
    is_main_site: boolean;
  }>;
};

type OrderHandoverSalesMetadataStaffOptionRaw = {
  user_id: string;
  username: string | null;
  display_name: string;
  main_dept_name: string | null;
  phone: string | null;
  disabled: boolean;
};

type OrderHandoverDetailRaw = {
  project_id: string;
  order_id: string | null;
  selected_customer_quote_id?: string | null;
  summary: {
    order_no: string;
    contract_no: string | null;
    team_id: string | null;
    route_name_snapshot?: string | null;
    order_status?: string | null;
    sign_type?: string | null;
    date_range: string;
    project_title: string;
    people_text: string;
    amount_text: string;
    source_text: string;
    ticket_sync_note: string;
  };
  selected_itinerary: {
    id: string;
    title: string;
    version: string | null;
    date_range: string;
    days: number;
    school: string | null;
    destination: string | null;
    student_count: number;
    teacher_count: number;
    guide_count: number;
    sales_user_name: string | null;
    dispatcher_user_name: string | null;
    major_names_text: string | null;
    is_confirmed?: boolean;
    pricing_finalized?: boolean;
  };
  itinerary_options?: Array<{
    id: string;
    title: string;
    version: string | null;
    date_range: string;
    days: number;
    school: string | null;
    destination: string | null;
    student_count: number;
    teacher_count: number;
    guide_count: number;
    sales_user_name: string | null;
    dispatcher_user_name: string | null;
    major_names_text: string | null;
    is_confirmed?: boolean;
    pricing_finalized?: boolean;
  }>;
  sales_handover: {
    sales_user_id: string | null;
    sales_user_name: string | null;
    updated_at: string | null;
    service_consultant_user_id: string | null;
    service_consultant_name: string | null;
    dispatcher_user_id: string | null;
    dispatcher_user_name: string | null;
    catch_order_user_id: string | null;
    catch_order_user_name: string | null;
    contract_signer_names: string | null;
    customer_novelty_type: string | null;
    major_names_text: string | null;
    school_site_text: string | null;
    order_total_amount_text: string;
    contract_template_type: string | null;
    is_advance_money: boolean | null;
    contract_people_count: number | null;
    contract_unit_price_amount: string | null;
    travel_fee_pay_time_text: string | null;
    receiving_account_text: string | null;
    receiving_remark: string | null;
    invoice_desc: string | null;
    local_invoice_amount_desc: string | null;
    sign_team_style: string | null;
    is_collect_transport_fee: boolean | null;
    collect_train_ticket_amount: string | null;
    collect_flight_ticket_amount: string | null;
    teacher_house_remark: string | null;
    teacher_food_remark: string | null;
    teacher_ticket_remark: string | null;
    teacher_transport_remark: string | null;
    guide_remark: string | null;
    is_collect_intangible_or_school_enterprise_fee: boolean | null;
    collect_intangible_amount: string | null;
    collect_school_enterprise_amount: string | null;
    teacher_local_pos_flag: boolean | null;
    gift_remark: string | null;
    banner_desc: string | null;
    banner_color: string | null;
    go_out_remark: string | null;
    other_remark: string | null;
    interview_remark: string | null;
    team_care_requirement: string | null;
    arbitration_desc: string | null;
    contract_modify_reason: string | null;
    single_order_service_fee?: string | null;
  };
  contract_signing?: OrderHandoverContractSigningRaw;
  roster: {
    id?: string | null;
    stats: {
      total: number;
      entered: number;
      student: number;
      teacher: number;
      entered_student?: number;
      entered_teacher?: number;
      guide: number;
    };
    status: string;
    source_mode: string;
    is_ticket_locked: boolean;
    accommodation_summary_text: string | null;
    vehicle_summary_text: string | null;
    special_marker_summary_text: string | null;
    last_imported_at: string | null;
    validation: {
      validation_status: string;
      error_count: number;
      duplicate_certificate_count: number;
      duplicate_name_phone_count: number;
      missing_required_count: number;
      completeness_percent: number;
      receivable_amount_text: string;
      amount_matched: boolean | null;
    };
    members: Array<{
      id: string;
      name: string;
      role: string;
      entry_source_type: string | null;
      sort_order: number | null;
      teacher_type: string | null;
      class_group: string | null;
      gender: string | null;
      age: number | null;
      birthday: string | null;
      certificate_type: string | null;
      certificate_no: string | null;
      certificate_image_url: string | null;
      student_no: string | null;
      phone: string | null;
      location: string | null;
      rooming: string | null;
      vehicle: string | null;
      vehicle_assignment: string | null;
      guidance_note: string | null;
      big_traffic_info: string | null;
      actual_receivable_amount: string | null;
      line_amount: string | null;
      is_signer: boolean;
      is_show_in_contract: boolean;
      is_team_linkman: boolean;
      is_main_signer: boolean;
      main_signer_address: string | null;
      member_state: string | null;
      is_stopped: boolean;
      remark: string | null;
    }>;
  };
  order_lines: Array<{
    id: string;
    line_name: string;
    line_type_code: string;
    source_mode?: string | null;
    resource_type?: string | null;
    people_count: number | null;
    quantity: string;
    unit: string;
    sales_unit_price_amount: string;
    sales_total_amount: string;
    cost_unit_price_amount?: string | null;
    cost_total_amount?: string | null;
    line_desc: string | null;
  }>;
};

type OrderHandoverCustomerQuoteOptionRaw = {
  customer_quote_id: string;
  version_no: number;
  version_name: string;
  status: "finalized";
  template_type: "transparent" | "standard" | "package";
  customer_payment_total_amount: string;
  agent_collection_total_amount: string;
  contract_receivable_amount: string;
  student_count: number;
  teacher_count: number;
  student_unit_price: string;
  teacher_unit_price: string;
  standard_quote_total_amount: string;
  updated_at: string;
};

type OrderHandoverContractSigningRaw = {
  contract_document_id: string | null;
  contract_no: string | null;
  external_contract_no: string | null;
  provider_code: string | null;
  provider_name: string | null;
  contract_template_type: string | null;
  sign_state: number;
  sign_state_code: string;
  sign_state_text: string;
  apply_time: string | null;
  sent_at: string | null;
  completed_at: string | null;
  invalidated_at: string | null;
  document_url: string | null;
  contract_download_url: string | null;
  can_apply: boolean;
  can_invalidate: boolean;
  blocked_reason: string | null;
  previous_voided_contract_no?: string | null;
  previous_voided_sign_state_text?: string | null;
  previous_voided_at?: string | null;
  sign_records: Array<{
    id: string;
    signer_member_id: string | null;
    signer_name: string;
    signer_phone: string | null;
    signer_certificate_no: string | null;
    signer_role: string | null;
    is_main_signer: boolean;
    is_sign: boolean;
    is_cancelled: boolean;
    sign_url: string | null;
    wait_sign_user_info: string | null;
    sign_send_time: string | null;
    sign_success_time: string | null;
    cancel_time: string | null;
    notification_channel_code?: string | null;
    notification_channel_text?: string | null;
    notification_state_code?: string | null;
    notification_state_text?: string | null;
  }>;
};

type OrderHandoverContractSignPortalRaw = {
  customization_project_id: string;
  sign_record_id: string;
  contract_no: string;
  document_name: string | null;
  provider_code: string;
  provider_name: string;
  contract_template_type: string | null;
  signer_name_masked: string;
  signer_phone_masked: string;
  signer_certificate_no_masked: string;
  is_main_signer: boolean;
  notification_channel_text: string;
  notification_state_text: string;
  sign_state_code: string;
  sign_state_text: string;
  is_sign: boolean;
  is_cancelled: boolean;
  sign_success_time: string | null;
  can_sign: boolean;
  blocked_reason: string | null;
  provider_action_type?: string | null;
  provider_action_url?: string | null;
  real_name_status_code?: string | null;
  real_name_status_text?: string | null;
};

type OrderHandoverRosterImportPreviewRaw = {
  mode: "merge" | "replace";
  summary: {
    total_rows: number;
    valid_rows: number;
    insert_count: number;
    update_count: number;
    stop_count: number;
    error_count: number;
    duplicate_certificate_count: number;
    duplicate_name_phone_count: number;
    missing_required_count: number;
  };
  rows: Array<{
    row_number: number;
    action: "insert" | "update" | "error";
    member_id: string | null;
    name: string;
    certificate_no: string | null;
    phone: string | null;
    role: string;
    class_group: string | null;
    teacher_type: string | null;
    actual_receivable_amount?: string | null;
    big_traffic_info?: string | null;
    guidance_note?: string | null;
    student_no?: string | null;
    age?: number | null;
    gender?: string | null;
    birthday?: string | null;
    main_signer_address?: string | null;
    issues: Array<{
      row_number: number;
      field: string | null;
      message: string;
    }>;
  }>;
  issues: Array<{
    row_number: number;
    field: string | null;
    message: string;
  }>;
};

/** 查询订单交接人员下拉候选。 */
export async function fetchOrderHandoverStaffOptions(): Promise<OrderHandoverStaffOption[]> {
  const response = await httpClient.get<ApiEnvelope<SysStaffListPayloadRaw>>("/sys/dept-users/staff", {
    params: {
      is_staff: true,
      page: 1,
      page_size: 100,
      sort_field: "update_time",
      sort_direction: "desc",
    },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("订单交接人员候选接口返回了无效数据");
  }
  return (response.data.data.items ?? []).map(mapStaffOption);
}

/** 查询销售交接编辑元数据。 */
export async function fetchOrderHandoverSalesMetadata(
  projectId: string,
): Promise<OrderHandoverSalesMetadata> {
  const response = await httpClient.get<ApiEnvelope<OrderHandoverSalesMetadataRaw>>(
    `/order-handover/projects/${projectId}/sales-handover/metadata`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("合同交接元数据接口返回了无效数据");
  }
  return mapSalesMetadata(response.data.data);
}

/** 查询订单交接院系专业下拉候选。 */
export async function fetchOrderHandoverMajorOptions(): Promise<OrderHandoverMajorOption[]> {
  const response = await httpClient.get<ApiEnvelope<DicMajorListPayloadRaw>>("/dic/majors", {
    params: {
      page: 1,
      page_size: 100,
      is_disabled: false,
      sort_field: "sort_no",
      sort_direction: "asc",
    },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("订单交接专业候选接口返回了无效数据");
  }
  return (response.data.data.items ?? []).map(mapMajorOption);
}

type GuideLaborContractRaw = {
  id: string;
  customization_project_id: string;
  order_id: string;
  itinerary_id: string;
  settlement_supplier_id: string;
  contract_no: string;
  guide_name: string;
  guide_phone?: string | null;
  sign_state: number;
  is_sign: boolean | number;
  sign_url?: string | null;
  document_url?: string | null;
  contract_download_url?: string | null;
  apply_time?: string | null;
  completed_at?: string | null;
};

/** 把导游劳务合同原始响应映射为前端领域对象。 */
function mapGuideLaborContract(raw: GuideLaborContractRaw): GuideLaborContract {
  return {
    id: raw.id,
    customizationProjectId: raw.customization_project_id,
    orderId: raw.order_id,
    itineraryId: raw.itinerary_id,
    settlementSupplierId: raw.settlement_supplier_id,
    contractNo: raw.contract_no,
    guideName: raw.guide_name,
    guidePhone: raw.guide_phone ?? null,
    signState: raw.sign_state,
    isSign: Boolean(raw.is_sign),
    signUrl: raw.sign_url ?? null,
    documentUrl: raw.document_url ?? null,
    contractDownloadUrl: raw.contract_download_url ?? null,
    applyTime: raw.apply_time ?? null,
    completedAt: raw.completed_at ?? null,
  };
}

/** 读取项目下某导游当前法大大劳务合同。 */
export async function fetchGuideLaborContract(
  projectId: string,
  settlementSupplierId: string,
): Promise<GuideLaborContract | null> {
  const response = await httpClient.get<ApiEnvelope<GuideLaborContractRaw | null>>(
    `/order-handover/projects/${encodeURIComponent(projectId)}/guide-labor-contracts`,
    { params: { settlement_supplier_id: settlementSupplierId } },
  );
  if (response.data.code !== "OK") {
    throw new Error("导游劳务合同查询接口返回了无效数据");
  }
  return response.data.data ? mapGuideLaborContract(response.data.data) : null;
}

/** 生成导游劳务法大大合同（ModelType=600）。 */
export async function generateGuideLaborContract(
  projectId: string,
  payload: GuideLaborContractGeneratePayload,
): Promise<GuideLaborContract> {
  const response = await httpClient.post<ApiEnvelope<GuideLaborContractRaw>>(
    `/order-handover/projects/${encodeURIComponent(projectId)}/guide-labor-contracts`,
    {
      settlement_supplier_id: payload.settlementSupplierId,
      guide_name: payload.guideName,
      fee_lines: payload.feeLines.map((line) => ({
        item_name: line.itemName,
        unit_price: line.unitPrice ?? null,
        amount: line.amount ?? null,
        line_no: line.lineNo ?? null,
        service_date_from: line.serviceDateFrom ?? null,
        service_date_to: line.serviceDateTo ?? null,
      })),
      team_type_label: payload.teamTypeLabel ?? "考察",
      replace_existing: payload.replaceExisting ?? true,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("导游劳务合同生成接口返回了无效数据");
  }
  return mapGuideLaborContract(response.data.data);
}

/** 查询订单交接详情。 */
export async function fetchOrderHandoverDetail(projectId: string): Promise<OrderHandoverDetail> {
  const response = await httpClient.get<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}`,
    { timeout: ORDER_HANDOVER_DETAIL_TIMEOUT_MS, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("订单交接详情接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 首次转订单时创建订单头，已有订单则由服务端幂等复用。 */
export async function createOrderHandoverDraftOrder(
  projectId: string,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${encodeURIComponent(projectId)}/order`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("转订单接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 查询当前签约行程可选择的已定稿成交报价。 */
export async function fetchOrderHandoverCustomerQuoteOptions(
  projectId: string,
): Promise<OrderHandoverCustomerQuoteOption[]> {
  const response = await httpClient.get<ApiEnvelope<OrderHandoverCustomerQuoteOptionRaw[]>>(
    `/order-handover/projects/${projectId}/customer-quote-options`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("成交报价候选接口返回了无效数据");
  }
  return response.data.data.map((option) => ({
    customerQuoteId: option.customer_quote_id,
    versionNo: option.version_no,
    versionName: option.version_name,
    status: option.status,
    templateType: option.template_type,
    customerPaymentTotalAmount: option.customer_payment_total_amount,
    agentCollectionTotalAmount: option.agent_collection_total_amount,
    contractReceivableAmount: option.contract_receivable_amount,
    studentCount: option.student_count,
    teacherCount: option.teacher_count,
    studentUnitPrice: option.student_unit_price,
    teacherUnitPrice: option.teacher_unit_price,
    standardQuoteTotalAmount: option.standard_quote_total_amount,
    updatedAt: option.updated_at,
  }));
}

/** 保存销售交接编辑字段。 */
export async function updateOrderHandoverSalesHandover(
  projectId: string,
  payload: OrderHandoverSalesHandoverUpdate,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.put<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/sales-handover`,
    {
      contract_signer_names: payload.contractSignerNames,
      major_names: payload.majorNames,
      school_site_text: payload.schoolSiteText,
      customer_novelty_type: payload.customerNoveltyType,
      sales_user_id: payload.salesUserId,
      sales_user_name_snapshot: payload.salesUserNameSnapshot,
      service_consultant_user_id: payload.serviceConsultantUserId,
      service_consultant_name_snapshot: payload.serviceConsultantNameSnapshot,
      dispatcher_user_id: payload.dispatcherUserId,
      dispatcher_user_name_snapshot: payload.dispatcherUserNameSnapshot,
      catch_order_user_id: payload.catchOrderUserId,
      catch_order_user_name_snapshot: payload.catchOrderUserNameSnapshot,
      contract_template_type: payload.contractTemplateType,
      is_advance_money: payload.isAdvanceMoney,
      contract_people_count: toNumberOrUndefined(payload.contractPeopleCount),
      contract_unit_price_amount: payload.contractUnitPriceAmount,
      travel_fee_pay_time_text: payload.travelFeePayTimeText,
      receiving_account_text: payload.receivingAccountText,
      receiving_remark: payload.receivingRemark,
      invoice_desc: payload.invoiceDesc,
      local_invoice_amount_desc: payload.localInvoiceAmountDesc,
      sign_team_style: payload.signTeamStyle,
      is_collect_transport_fee: payload.isCollectTransportFee,
      collect_train_ticket_amount: payload.collectTrainTicketAmount,
      collect_flight_ticket_amount: payload.collectFlightTicketAmount,
      teacher_house_remark: payload.teacherHouseRemark,
      teacher_food_remark: payload.teacherFoodRemark,
      teacher_ticket_remark: payload.teacherTicketRemark,
      teacher_transport_remark: payload.teacherTransportRemark,
      guide_remark: payload.guideRemark,
      is_collect_intangible_or_school_enterprise_fee:
        payload.isCollectIntangibleOrSchoolEnterpriseFee,
      collect_intangible_amount: payload.collectIntangibleAmount,
      collect_school_enterprise_amount: payload.collectSchoolEnterpriseAmount,
      teacher_local_pos_flag: payload.teacherLocalPosFlag,
      gift_remark: payload.giftRemark,
      banner_desc: payload.bannerDesc,
      banner_color: payload.bannerColor,
      go_out_remark: payload.goOutRemark,
      other_remark: payload.otherRemark,
      interview_remark: payload.interviewRemark,
      team_care_requirement: payload.teamCareRequirement,
      arbitration_desc: payload.arbitrationDesc,
      contract_modify_reason: payload.contractModifyReason,
      single_order_service_fee: payload.singleOrderServiceFee,
      single_order_pricing_lines: payload.singleOrderPricingLines?.map((line) => ({
        id: line.id,
        sales_price_amount: line.salesPriceAmount,
      })),
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("合同交接保存接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 定稿或取消定稿销售交接。 */
export async function updateOrderHandoverSalesFinalize(
  projectId: string,
  finalized: boolean,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.put<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/sales-handover/finalize`,
    { finalized },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("合同交接定稿接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 合同挂网，进入合同签订阶段。 */
export async function publishOrderHandoverContract(
  projectId: string,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/contract/publish`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("合同挂网接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 取消挂网：订单状态回退草稿，名单/行程/核价定稿保持不变。 */
export async function unpublishOrderHandoverContract(
  projectId: string,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/contract/unpublish`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("取消挂网接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 一键取消挂网，并取消合同交接/名单/行程/核价定稿。 */
export async function unpublishAllOrderHandoverLocks(
  projectId: string,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/contract/unpublish-all`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("一键取消定稿接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 申请合同签订。 */
export async function applyOrderHandoverContract(
  projectId: string,
  payload: OrderHandoverContractApplyPayload = {},
): Promise<OrderHandoverDetail> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/contract/apply`,
    omitUndefinedRequestPayload({
      dry_run: payload.dryRun,
    }),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("合同签订申请接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 重新生成签署链接并按配置发送短信。 */
export async function issueOrderHandoverContractSignInvitations(
  projectId: string,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/contract/sign-invitations`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("签署通知发送接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 重发单个签约人短信。 */
export async function resendOrderHandoverContractSignSms(
  projectId: string,
  signRecordId: string,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/contract/sign-records/${signRecordId}/resend-sms`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("重发签约短信接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 申请作废当前合同。 */
export async function invalidateOrderHandoverContract(
  projectId: string,
  payload: OrderHandoverContractInvalidatePayload = {},
): Promise<OrderHandoverDetail> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/contract/invalidate`,
    omitUndefinedRequestPayload({
      reason: payload.reason,
    }),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("合同作废接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 查询本地合同签署入口的脱敏信息。 */
export async function fetchOrderHandoverContractSignPortal(
  signRecordId: string,
): Promise<OrderHandoverContractSignPortal> {
  const response = await httpClient.get<ApiEnvelope<OrderHandoverContractSignPortalRaw>>(
    `/order-handover/contracts/sign-records/${signRecordId}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("合同签署入口接口返回了无效数据");
  }
  return mapContractSignPortal(response.data.data);
}

/** 历史开发测试用的本地模拟签署接口，正式页面不调用。 */
export async function simulateOrderHandoverContractSign(
  signRecordId: string,
  payload: OrderHandoverContractSignSimulationPayload,
): Promise<OrderHandoverContractSignPortal> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverContractSignPortalRaw>>(
    `/order-handover/contracts/sign-records/${signRecordId}/simulate-sign`,
    {
      signer_name: payload.signerName,
      signer_phone: payload.signerPhone,
      signer_certificate_no: payload.signerCertificateNo,
      agreed_to_terms: payload.agreedToTerms,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("合同签署接口返回了无效数据");
  }
  return mapContractSignPortal(response.data.data);
}

/** 核验签约人冻结身份并进入法大大正式实名认证或签署页面。 */
export async function prepareOrderHandoverContractSign(
  signRecordId: string,
  payload: OrderHandoverContractSignPreparePayload,
): Promise<OrderHandoverContractSignPortal> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverContractSignPortalRaw>>(
    `/order-handover/contracts/sign-records/${signRecordId}/prepare-sign`,
    {
      signer_name: payload.signerName,
      signer_phone: payload.signerPhone,
      signer_certificate_no: payload.signerCertificateNo,
      agreed_to_terms: payload.agreedToTerms,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("法大大签署准备接口返回了无效数据");
  }
  return mapContractSignPortal(response.data.data);
}

/** 保存当前订单选择的签约行程。 */
export async function selectOrderHandoverItinerary(
  projectId: string,
  selectedItineraryId: string,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.put<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/selected-itinerary`,
    {
      selected_itinerary_id: selectedItineraryId,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("行程选择保存接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 选择订单成交采用的已定稿报价。 */
export async function selectOrderHandoverCustomerQuote(
  projectId: string,
  customerQuoteId: string,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.put<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/selected-customer-quote`,
    { customer_quote_id: customerQuoteId },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("成交报价选择接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 预览出行名单导入结果。 */
export async function previewOrderHandoverRosterImport(
  projectId: string,
  payload: OrderHandoverRosterImportPayload,
): Promise<OrderHandoverRosterImportPreview> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverRosterImportPreviewRaw>>(
    `/order-handover/projects/${projectId}/roster/import/preview`,
    toRosterImportRequest(payload),
    { timeout: ROSTER_IMPORT_TIMEOUT_MS, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("出行名单导入预览接口返回了无效数据");
  }
  return mapRosterImportPreview(response.data.data);
}

/** 提交出行名单导入结果。 */
export async function commitOrderHandoverRosterImport(
  projectId: string,
  payload: OrderHandoverRosterImportPayload,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/roster/import`,
    toRosterImportRequest(payload),
    { timeout: ROSTER_IMPORT_TIMEOUT_MS, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("出行名单导入保存接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 批量更新出行名单合同标记。 */
export async function updateOrderHandoverRosterMemberFlags(
  projectId: string,
  payload: OrderHandoverRosterMemberFlagsPayload,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.put<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/roster/member-flags`,
    {
      member_ids: payload.memberIds,
      is_signer: payload.isSigner,
      is_show_in_contract: payload.isShowInContract,
      is_team_linkman: payload.isTeamLinkman,
      is_main_signer: payload.isMainSigner,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("出行名单批量标记接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 停用或启用出行名单成员。 */
export async function updateOrderHandoverRosterMemberStop(
  projectId: string,
  memberId: string,
  isStopped: boolean,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.put<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/roster/members/${memberId}/stop`,
    { is_stopped: isStopped },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("出行名单停用接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 批量停用或启用出行名单成员。 */
export async function updateOrderHandoverRosterMemberStops(
  projectId: string,
  payload: OrderHandoverRosterMemberStopsPayload,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.put<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/roster/member-stops`,
    {
      member_ids: payload.memberIds,
      is_stopped: payload.isStopped,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("出行名单批量停用接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 在线保存单个出行名单成员。 */
export async function updateOrderHandoverRosterMember(
  projectId: string,
  memberId: string,
  payload: OrderHandoverRosterMemberUpdatePayload,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.put<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/roster/members/${memberId}`,
    toRosterMemberRequest(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("出行名单成员保存接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 新增单个出行名单成员。 */
export async function createOrderHandoverRosterMember(
  projectId: string,
  payload: OrderHandoverRosterMemberCreatePayload,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.post<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/roster/members`,
    toRosterMemberRequest(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("出行名单成员新增接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 定稿或取消定稿出行名单。 */
export async function updateOrderHandoverRosterFinalize(
  projectId: string,
  finalized: boolean,
): Promise<OrderHandoverDetail> {
  const response = await httpClient.put<ApiEnvelope<OrderHandoverDetailRaw>>(
    `/order-handover/projects/${projectId}/roster/finalize`,
    { finalized },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("出行名单定稿接口返回了无效数据");
  }
  return mapOrderHandoverDetail(response.data.data);
}

/** 删除请求体里的 undefined 字段，保持 PATCH 式更新语义。 */
function omitUndefinedRequestPayload<T extends Record<string, unknown>>(payload: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

/** 转换名单成员新增/编辑请求为后端字段。 */
function toRosterMemberRequest(
  payload: OrderHandoverRosterMemberUpdatePayload,
) {
  return omitUndefinedRequestPayload({
    member_name: payload.memberName,
    traveler_type_label: payload.travelerTypeLabel,
    teacher_type_label: payload.teacherTypeLabel,
    class_group_label: payload.classGroupLabel,
    traffic_type_label: payload.trafficTypeLabel,
    vehicle_assignment_label: payload.vehicleAssignmentLabel,
    certificate_type: payload.certificateType,
    certificate_no: payload.certificateNo,
    student_no: payload.studentNo,
    mobile: payload.mobile,
    gender: payload.gender,
    age: payload.age,
    birthday: payload.birthday,
    rooming_label: payload.roomingLabel,
    big_traffic_info: payload.bigTrafficInfo,
    guidance_note: payload.guidanceNote,
    actual_receivable_amount: payload.actualReceivableAmount,
    line_amount: payload.lineAmount,
    main_signer_address: payload.mainSignerAddress,
    remark: payload.remark,
  });
}

/** 导出出行名单文件。 */
export async function exportOrderHandoverRoster(
  projectId: string,
  exportType: OrderHandoverRosterExportType,
  options?: OrderHandoverRosterExportOptions,
): Promise<void> {
  if (exportType === "standard" && options) {
    downloadRosterBlob(
      new Blob([`\uFEFF${buildFeeAdjustmentRosterCsv(options)}`], {
        type: "text/csv;charset=utf-8",
      }),
      "人员名单信息.csv",
    );
    return;
  }
  const response = await httpClient.get<Blob>(
    `/order-handover/projects/${projectId}/roster/export`,
    {
      params: { export_type: exportType },
      responseType: "blob",
    },
  );
  const blob = response.data;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = exportType === "guide" ? "导游导出人员信息.csv" : exportType === "template" ? rosterStandardTemplateFileName : "人员名单信息.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}


/** 使用页面名单列序生成 Excel 可直接打开的标准 CSV。 */
export function buildFeeAdjustmentRosterCsv(options: OrderHandoverRosterExportOptions): string {
  const adjustmentByMemberId = new Map(
    options.feeAdjustments.map((item) => [item.rosterMemberId, item]),
  );
  const includeFee = Boolean(options.includeFeeAdjustmentColumns);
  const headers = includeFee
    ? [
        "序号",
        "姓名",
        "人员类型",
        "证件号",
        "电话",
        "专业/班级类型",
        "职务/职位",
        "冻结基准应收",
        "费用调整",
        "当前个人应收",
        "火车硬座硬卧",
        "特殊情况",
        "学号",
        "年龄",
        "性别",
        "生日",
        "主签合同人地址",
        "备注",
        "标记",
      ]
    : [
        "序号",
        "姓名",
        "人员类型",
        "证件号",
        "电话",
        "专业/班级类型",
        "职务/职位",
        "应收金额",
        "火车硬座硬卧",
        "特殊情况",
        "学号",
        "年龄",
        "性别",
        "生日",
        "主签合同人地址",
        "备注",
        "标记",
      ];
  const rows: Array<Array<string | number | undefined>> = [
    headers,
    ...options.members.map((member, index) => {
      const adjustment = adjustmentByMemberId.get(member.id);
      const baseAmount = adjustment?.baseReceivableAmount ?? member.actualReceivableAmount;
      const commonTail = [
        member.bigTrafficInfo,
        member.guidanceNote,
        member.studentNo,
        member.age > 0 ? member.age : undefined,
        member.gender,
        member.birthday,
        member.mainSignerAddress,
        member.remark,
        formatRosterExportTags(member),
      ];
      if (includeFee) {
        return [
          index + 1,
          member.name,
          member.role,
          member.certificateNo,
          member.phone,
          member.classGroup,
          member.teacherType,
          baseAmount,
          adjustment?.processedReceivableAdjustmentAmount,
          adjustment?.finalReceivableAmount,
          ...commonTail,
        ];
      }
      return [
        index + 1,
        member.name,
        member.role,
        member.certificateNo,
        member.phone,
        member.classGroup,
        member.teacherType,
        member.actualReceivableAmount,
        ...commonTail,
      ];
    }),
  ];
  return rows.map((row) => row.map(toCsvCell).join(",")).join("\n");
}

/** 导出标记列文案，顺序与网页标记展示一致。 */
function formatRosterExportTags(member: OrderRosterMember): string {
  const tags: string[] = [];
  if (member.isMainSigner) {
    tags.push("主签合同人");
  }
  if (member.isSigner) {
    tags.push("签合同");
  }
  if (!member.isShowInContract) {
    tags.push("不进合同");
  }
  if (member.isTeamLinkman) {
    tags.push("主要联系人");
  }
  return tags.join("、");
}

/** 按 RFC 4180 规则转义 CSV 单元格。 */
function toCsvCell(value: string | number | undefined): string {
  const text = value === undefined ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/** 触发浏览器下载并及时释放对象 URL。 */
function downloadRosterBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/** 映射接口详情响应为前端详情模型。 */
function mapOrderHandoverDetail(raw: OrderHandoverDetailRaw): OrderHandoverDetail {
  return {
    projectId: raw.project_id,
    orderId: raw.order_id ?? undefined,
    selectedCustomerQuoteId: raw.selected_customer_quote_id ?? undefined,
    summary: {
      orderNo: raw.summary.order_no,
      contractNo: raw.summary.contract_no ?? undefined,
      teamId: raw.summary.team_id ?? undefined,
      routeNameSnapshot: raw.summary.route_name_snapshot ?? undefined,
      orderStatus: raw.summary.order_status ?? undefined,
      signType: raw.summary.sign_type ?? undefined,
      dateRange: raw.summary.date_range,
      projectTitle: raw.summary.project_title,
      peopleText: raw.summary.people_text,
      amountText: raw.summary.amount_text,
      sourceText: raw.summary.source_text,
      ticketSyncNote: raw.summary.ticket_sync_note,
    },
    selectedItinerary: mapItinerary(raw.selected_itinerary),
    itineraryOptions: raw.itinerary_options?.map(mapItinerary) ?? [],
    salesHandover: {
      salesUserId: raw.sales_handover.sales_user_id ?? undefined,
      salesUserName: raw.sales_handover.sales_user_name ?? undefined,
      updatedAt: raw.sales_handover.updated_at ?? undefined,
      serviceConsultantUserId: raw.sales_handover.service_consultant_user_id ?? undefined,
      serviceConsultantName: raw.sales_handover.service_consultant_name ?? undefined,
      dispatcherUserId: raw.sales_handover.dispatcher_user_id ?? undefined,
      dispatcherUserName: raw.sales_handover.dispatcher_user_name ?? undefined,
      catchOrderUserId: raw.sales_handover.catch_order_user_id ?? undefined,
      catchOrderUserName: raw.sales_handover.catch_order_user_name ?? undefined,
      contractSignerNames: raw.sales_handover.contract_signer_names ?? undefined,
      customerNoveltyType: raw.sales_handover.customer_novelty_type ?? undefined,
      majorNamesText: raw.sales_handover.major_names_text ?? undefined,
      schoolSiteText: raw.sales_handover.school_site_text ?? undefined,
      orderTotalAmountText: raw.sales_handover.order_total_amount_text,
      contractTemplateType: raw.sales_handover.contract_template_type ?? undefined,
      isAdvanceMoney: raw.sales_handover.is_advance_money ?? undefined,
      contractPeopleCount: raw.sales_handover.contract_people_count ?? undefined,
      contractUnitPriceAmount: raw.sales_handover.contract_unit_price_amount ?? undefined,
      travelFeePayTimeText: raw.sales_handover.travel_fee_pay_time_text ?? undefined,
      receivingAccountText: raw.sales_handover.receiving_account_text ?? undefined,
      receivingRemark: raw.sales_handover.receiving_remark ?? undefined,
      invoiceDesc: raw.sales_handover.invoice_desc ?? undefined,
      localInvoiceAmountDesc: raw.sales_handover.local_invoice_amount_desc ?? undefined,
      signTeamStyle: raw.sales_handover.sign_team_style ?? undefined,
      isCollectTransportFee: raw.sales_handover.is_collect_transport_fee ?? undefined,
      collectTrainTicketAmount: raw.sales_handover.collect_train_ticket_amount ?? undefined,
      collectFlightTicketAmount: raw.sales_handover.collect_flight_ticket_amount ?? undefined,
      teacherHouseRemark: raw.sales_handover.teacher_house_remark ?? undefined,
      teacherFoodRemark: raw.sales_handover.teacher_food_remark ?? undefined,
      teacherTicketRemark: raw.sales_handover.teacher_ticket_remark ?? undefined,
      teacherTransportRemark: raw.sales_handover.teacher_transport_remark ?? undefined,
      guideRemark: raw.sales_handover.guide_remark ?? undefined,
      isCollectIntangibleOrSchoolEnterpriseFee:
        raw.sales_handover.is_collect_intangible_or_school_enterprise_fee ?? undefined,
      collectIntangibleAmount: raw.sales_handover.collect_intangible_amount ?? undefined,
      collectSchoolEnterpriseAmount:
        raw.sales_handover.collect_school_enterprise_amount ?? undefined,
      teacherLocalPosFlag: raw.sales_handover.teacher_local_pos_flag ?? undefined,
      giftRemark: raw.sales_handover.gift_remark ?? undefined,
      bannerDesc: raw.sales_handover.banner_desc ?? undefined,
      bannerColor: raw.sales_handover.banner_color ?? undefined,
      goOutRemark: raw.sales_handover.go_out_remark ?? undefined,
      otherRemark: raw.sales_handover.other_remark ?? undefined,
      interviewRemark: raw.sales_handover.interview_remark ?? undefined,
      teamCareRequirement: raw.sales_handover.team_care_requirement ?? undefined,
      arbitrationDesc: raw.sales_handover.arbitration_desc ?? undefined,
      contractModifyReason: raw.sales_handover.contract_modify_reason ?? undefined,
      singleOrderServiceFee: raw.sales_handover.single_order_service_fee ?? undefined,
    },
    contractSigning: mapContractSigning(raw.contract_signing),
    roster: {
      id: raw.roster.id ?? undefined,
      stats: {
        total: raw.roster.stats.total,
        entered: raw.roster.stats.entered,
        student: raw.roster.stats.student,
        teacher: raw.roster.stats.teacher,
        enteredStudent: raw.roster.stats.entered_student ?? 0,
        enteredTeacher: raw.roster.stats.entered_teacher ?? 0,
        guide: raw.roster.stats.guide,
      },
      status: raw.roster.status,
      sourceMode: raw.roster.source_mode,
      isTicketLocked: raw.roster.is_ticket_locked,
      accommodationSummaryText: raw.roster.accommodation_summary_text ?? undefined,
      vehicleSummaryText: raw.roster.vehicle_summary_text ?? undefined,
      specialMarkerSummaryText: raw.roster.special_marker_summary_text ?? undefined,
      lastImportedAt: raw.roster.last_imported_at ?? undefined,
      validation: {
        validationStatus: raw.roster.validation.validation_status,
        errorCount: raw.roster.validation.error_count,
        duplicateCertificateCount: raw.roster.validation.duplicate_certificate_count,
        duplicateNamePhoneCount: raw.roster.validation.duplicate_name_phone_count,
        missingRequiredCount: raw.roster.validation.missing_required_count,
        completenessPercent: raw.roster.validation.completeness_percent,
        receivableAmountText: raw.roster.validation.receivable_amount_text,
        amountMatched: raw.roster.validation.amount_matched ?? undefined,
      },
      members: raw.roster.members.map(mapRosterMember),
    },
    orderLines: raw.order_lines.map(mapOrderLine),
  };
}

/** 映射合同签订聚合状态。 */
function mapContractSigning(
  raw: OrderHandoverContractSigningRaw | undefined,
): OrderHandoverContractSigning {
  if (!raw) {
    return {
      signState: 1,
      signStateCode: "not_applied",
      signStateText: "未申请",
      canApply: false,
      canInvalidate: false,
      signRecords: [],
    };
  }
  return {
    contractDocumentId: raw.contract_document_id ?? undefined,
    contractNo: raw.contract_no ?? undefined,
    externalContractNo: raw.external_contract_no ?? undefined,
    providerCode: raw.provider_code ?? undefined,
    providerName: raw.provider_name ?? undefined,
    contractTemplateType: raw.contract_template_type ?? undefined,
    signState: raw.sign_state,
    signStateCode: raw.sign_state_code,
    signStateText: raw.sign_state_text,
    applyTime: raw.apply_time ?? undefined,
    sentAt: raw.sent_at ?? undefined,
    completedAt: raw.completed_at ?? undefined,
    invalidatedAt: raw.invalidated_at ?? undefined,
    documentUrl: raw.document_url ?? undefined,
    contractDownloadUrl: raw.contract_download_url ?? undefined,
    canApply: raw.can_apply,
    canInvalidate: raw.can_invalidate,
    blockedReason: raw.blocked_reason ?? undefined,
    previousVoidedContractNo: raw.previous_voided_contract_no ?? undefined,
    previousVoidedSignStateText: raw.previous_voided_sign_state_text ?? undefined,
    previousVoidedAt: raw.previous_voided_at ?? undefined,
    signRecords: raw.sign_records.map((record) => ({
      id: record.id,
      signerMemberId: record.signer_member_id ?? undefined,
      signerName: record.signer_name,
      signerPhone: record.signer_phone ?? undefined,
      signerCertificateNo: record.signer_certificate_no ?? undefined,
      signerRole: record.signer_role ?? undefined,
      isMainSigner: record.is_main_signer,
      isSign: record.is_sign,
      isCancelled: record.is_cancelled,
      signUrl: record.sign_url ?? undefined,
      waitSignUserInfo: record.wait_sign_user_info ?? undefined,
      signSendTime: record.sign_send_time ?? undefined,
      signSuccessTime: record.sign_success_time ?? undefined,
      cancelTime: record.cancel_time ?? undefined,
      notificationChannelCode: record.notification_channel_code ?? "none",
      notificationChannelText: record.notification_channel_text ?? "未配置",
      notificationStateCode: record.notification_state_code ?? "pending",
      notificationStateText: record.notification_state_text ?? "待发送",
    })),
  };
}

/** 映射独立合同签署入口。 */
function mapContractSignPortal(
  raw: OrderHandoverContractSignPortalRaw,
): OrderHandoverContractSignPortal {
  return {
    customizationProjectId: raw.customization_project_id,
    signRecordId: raw.sign_record_id,
    contractNo: raw.contract_no,
    documentName: raw.document_name ?? undefined,
    providerCode: raw.provider_code,
    providerName: raw.provider_name,
    contractTemplateType: raw.contract_template_type ?? undefined,
    signerNameMasked: raw.signer_name_masked,
    signerPhoneMasked: raw.signer_phone_masked,
    signerCertificateNoMasked: raw.signer_certificate_no_masked,
    isMainSigner: raw.is_main_signer,
    notificationChannelText: raw.notification_channel_text,
    notificationStateText: raw.notification_state_text,
    signStateCode: raw.sign_state_code,
    signStateText: raw.sign_state_text,
    isSign: raw.is_sign,
    isCancelled: raw.is_cancelled,
    signSuccessTime: raw.sign_success_time ?? undefined,
    canSign: raw.can_sign,
    blockedReason: raw.blocked_reason ?? undefined,
    providerActionType: raw.provider_action_type ?? undefined,
    providerActionUrl: raw.provider_action_url ?? undefined,
    realNameStatusCode: raw.real_name_status_code ?? undefined,
    realNameStatusText: raw.real_name_status_text ?? undefined,
  };
}

/** 转换名单导入请求为后端字段。 */
function toRosterImportRequest(payload: OrderHandoverRosterImportPayload) {
  return {
    mode: payload.mode,
    parse_mode: payload.parseMode,
    text: payload.text,
    file_name: payload.fileName,
    file_content_base64: payload.fileContentBase64,
    files: payload.files?.map((file) => ({
      file_name: file.fileName,
      file_content_base64: file.fileContentBase64,
    })),
    edited_rows: payload.editedRows?.map((row) => ({
      row_number: row.rowNumber,
      member_id: row.memberId,
      name: row.name,
      certificate_no: row.certificateNo,
      phone: row.phone,
      class_group: row.classGroup,
      teacher_type: row.teacherType,
      actual_receivable_amount: row.actualReceivableAmount,
      big_traffic_info: row.bigTrafficInfo,
      guidance_note: row.guidanceNote,
      student_no: row.studentNo,
      role: row.role,
      age: row.age,
      gender: row.gender,
      birthday: row.birthday,
      main_signer_address: row.mainSignerAddress,
    })),
  };
}

/** 映射名单导入预览结果。 */
function mapRosterImportPreview(
  raw: OrderHandoverRosterImportPreviewRaw,
): OrderHandoverRosterImportPreview {
  return {
    mode: raw.mode,
    summary: {
      totalRows: raw.summary.total_rows,
      validRows: raw.summary.valid_rows,
      insertCount: raw.summary.insert_count,
      updateCount: raw.summary.update_count,
      stopCount: raw.summary.stop_count,
      errorCount: raw.summary.error_count,
      duplicateCertificateCount: raw.summary.duplicate_certificate_count,
      duplicateNamePhoneCount: raw.summary.duplicate_name_phone_count,
      missingRequiredCount: raw.summary.missing_required_count,
    },
    rows: raw.rows.map((row) => ({
      rowNumber: row.row_number,
      action: row.action,
      memberId: row.member_id ?? undefined,
      name: row.name,
      certificateNo: row.certificate_no ?? undefined,
      phone: row.phone ?? undefined,
      role: row.role,
      classGroup: row.class_group ?? undefined,
      teacherType: row.teacher_type ?? undefined,
      actualReceivableAmount: row.actual_receivable_amount ?? undefined,
      bigTrafficInfo: row.big_traffic_info ?? undefined,
      guidanceNote: row.guidance_note ?? undefined,
      studentNo: row.student_no ?? undefined,
      age: row.age ?? undefined,
      gender: row.gender ?? undefined,
      birthday: row.birthday ?? undefined,
      mainSignerAddress: row.main_signer_address ?? undefined,
      issues: row.issues.map(mapRosterImportIssue),
    })),
    issues: raw.issues.map(mapRosterImportIssue),
  };
}

/** 映射名单导入行级问题。 */
function mapRosterImportIssue(
  raw: OrderHandoverRosterImportPreviewRaw["issues"][number],
) {
  return {
    rowNumber: raw.row_number,
    field: raw.field ?? undefined,
    message: raw.message,
  };
}

/** 映射销售交接编辑元数据。 */
function mapSalesMetadata(raw: OrderHandoverSalesMetadataRaw): OrderHandoverSalesMetadata {
  return {
    serviceConsultantOptions: raw.service_consultant_options.map(
      mapSalesMetadataStaffOption,
    ),
    staffOptions: raw.staff_options.map(mapSalesMetadataStaffOption),
    schoolSiteOptions: raw.school_site_options.map((option) => ({
      value: option.value,
      siteId: option.site_id ?? undefined,
      siteName: option.site_name ?? undefined,
      address: option.address ?? undefined,
      isMainSite: option.is_main_site,
    })),
  };
}

/** 映射销售交接人员候选。 */
function mapSalesMetadataStaffOption(
  raw: OrderHandoverSalesMetadataStaffOptionRaw,
): OrderHandoverStaffOption {
  return {
    id: raw.user_id,
    username: raw.username ?? undefined,
    displayName: raw.display_name || raw.username || `员工 ${raw.user_id}`,
    mainDeptName: raw.main_dept_name ?? undefined,
    phone: raw.phone ?? undefined,
    disabled: raw.disabled,
  };
}

/** 映射系统员工为订单交接下拉候选。 */
function mapStaffOption(raw: NonNullable<SysStaffListPayloadRaw["items"]>[number]): OrderHandoverStaffOption {
  return {
    id: String(raw.id),
    username: raw.username,
    displayName: raw.realname || raw.nickname || raw.username || `员工 ${raw.id}`,
    mainDeptName: raw.main_dept_name ?? "",
    phone: undefined,
    disabled: raw.status !== 1,
  };
}

/** 映射标准专业为订单交接多选候选。 */
function mapMajorOption(raw: NonNullable<DicMajorListPayloadRaw["items"]>[number]): OrderHandoverMajorOption {
  return {
    id: raw.major_id,
    majorName: raw.major_name,
    majorCategory: raw.major_category,
    disabled: raw.is_disabled,
  };
}

/** 映射接口行程摘要为前端行程选项。 */
function mapItinerary(raw: OrderHandoverDetailRaw["selected_itinerary"]): HandoverItineraryOption {
  return {
    id: raw.id,
    title: raw.title,
    version: raw.version ?? "当前",
    dateRange: raw.date_range,
    days: raw.days,
    school: raw.school ?? "未填写",
    destination: raw.destination ?? "待确认",
    studentCount: raw.student_count,
    teacherCount: raw.teacher_count,
    guideCount: raw.guide_count,
    salesUserName: raw.sales_user_name ?? "待选择",
    dispatcherUserName: raw.dispatcher_user_name ?? "待选择",
    majorNamesText: raw.major_names_text ?? undefined,
    isConfirmed: raw.is_confirmed ?? false,
    pricingFinalized: raw.pricing_finalized ?? false,
  };
}

/** 把表单数字文本转换为接口数字值，空值不提交。 */
function toNumberOrUndefined(value: string | undefined): number | undefined {
  if (value === undefined || value.trim() === "") {
    return undefined;
  }
  return Number(value);
}

/** 映射接口名单成员为前端表格行。 */
function mapRosterMember(raw: OrderHandoverDetailRaw["roster"]["members"][number]): OrderRosterMember {
  return {
    id: raw.id,
    name: raw.name,
    role: raw.role,
    entrySourceType: raw.entry_source_type ?? undefined,
    sortOrder: raw.sort_order ?? undefined,
    teacherType: raw.teacher_type ?? undefined,
    classGroup: raw.class_group ?? undefined,
    gender: raw.gender ?? "-",
    age: raw.age ?? 0,
    birthday: raw.birthday ?? undefined,
    certificateType: raw.certificate_type ?? "-",
    certificateNo: raw.certificate_no ?? "-",
    certificateImageUrl: raw.certificate_image_url ?? undefined,
    studentNo: raw.student_no ?? undefined,
    phone: raw.phone ?? "-",
    location: raw.location ?? undefined,
    rooming: raw.rooming ?? "-",
    vehicle: raw.vehicle ?? "-",
    vehicleAssignment: raw.vehicle_assignment ?? undefined,
    guidanceNote: raw.guidance_note ?? undefined,
    bigTrafficInfo: raw.big_traffic_info ?? undefined,
    actualReceivableAmount: raw.actual_receivable_amount ?? undefined,
    lineAmount: raw.line_amount ?? undefined,
    isSigner: raw.is_signer,
    isShowInContract: raw.is_show_in_contract,
    isTeamLinkman: raw.is_team_linkman,
    isMainSigner: raw.is_main_signer,
    mainSignerAddress: raw.main_signer_address ?? undefined,
    memberState: raw.member_state ?? undefined,
    isStopped: raw.is_stopped,
    remark: raw.remark ?? "-",
  };
}

/** 映射接口订单销售行为前端展示结构。 */
function mapOrderLine(raw: OrderHandoverDetailRaw["order_lines"][number]): OrderHandoverOrderLine {
  return {
    id: raw.id,
    lineName: raw.line_name,
    lineTypeCode: raw.line_type_code,
    sourceMode: raw.source_mode ?? undefined,
    resourceType: raw.resource_type ?? undefined,
    peopleCount: raw.people_count ?? undefined,
    quantity: raw.quantity,
    unit: raw.unit,
    salesUnitPriceAmount: raw.sales_unit_price_amount,
    salesTotalAmount: raw.sales_total_amount,
    costUnitPriceAmount: raw.cost_unit_price_amount ?? undefined,
    costTotalAmount: raw.cost_total_amount ?? undefined,
    lineDesc: raw.line_desc ?? undefined,
  };
}
